import type { ApiError } from "./client"
import i18n from "./i18n"

type LicensePolicyErrorBody = {
  code?: string
  error?: string
  message?: string
  detail?: string | Array<{ msg?: string }>
  license_status?: string
  degradation_mode?: string
  feature_key?: string
  limit?: number
  active_users?: number
}

const translateApiErrorCode = (body: LicensePolicyErrorBody): string | null => {
  if (body.code === "saas_active_users_limit_reached") {
    return i18n.t("errors.codes.saas_active_users_limit_reached", {
      limit: body.limit ?? 0,
      count: body.active_users ?? 0,
      defaultValue: "Active users limit reached.",
    })
  }

  if (body.code === "cooldown_active_reactivation") {
    return i18n.t("errors.codes.cooldown_active_reactivation", {
      defaultValue: "User cannot be reactivated yet.",
    })
  }

  return null
}

export const emailPattern = {
  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
  message: "Invalid email address",
}

export const namePattern = {
  value: /^[A-Za-z\s\u00C0-\u017F]{1,30}$/,
  message: "Invalid name",
}

export const domainPattern = {
  value: /^[a-zA-Z0-9-]+$/,
  message: "Invalid domain",
}

export const passwordRules = (isRequired = true) => {
  const rules: any = {
    minLength: {
      value: 8,
      message: "Password must be at least 8 characters",
    },
  }

  if (isRequired) {
    rules.required = "Password is required"
  }

  return rules
}

export const confirmPasswordRules = (
  getValues: () => any,
  isRequired = true,
) => {
  const rules: any = {
    validate: (value: string) => {
      const password = getValues().password || getValues().newPassword
      return value === password ? true : "The passwords do not match"
    },
  }

  if (isRequired) {
    rules.required = "Password confirmation is required"
  }

  return rules
}

export const getApiErrorMessage = (err: unknown): string => {
  const apiError = err as ApiError | undefined
  const body = (apiError?.body || {}) as LicensePolicyErrorBody

  const translatedCodeMessage = translateApiErrorCode(body)
  if (translatedCodeMessage) {
    return translatedCodeMessage
  }

  if (body.license_status || body.degradation_mode) {
    const parts = [body.error || i18n.t("errors.licenseBlocked", { defaultValue: "Action is blocked by license policy" })]
    if (body.license_status) {
      parts.push(`status: ${body.license_status}`)
    }
    if (body.degradation_mode) {
      parts.push(`mode: ${body.degradation_mode}`)
    }
    if (body.feature_key) {
      parts.push(`feature: ${body.feature_key}`)
    }
    return parts.join(" | ")
  }

  const errDetail = body.detail
  if (Array.isArray(errDetail) && errDetail.length > 0) {
    return errDetail[0].msg || "Something went wrong."
  }
  if (typeof errDetail === "string" && errDetail.trim()) {
    return errDetail
  }
  if (typeof body.message === "string" && body.message.trim()) {
    return body.message
  }
  if (typeof body.error === "string" && body.error.trim()) {
    return body.error
  }
  if (apiError?.message) {
    return apiError.message
  }
  return "Something went wrong."
}

export const getApiErrorTitle = (err: unknown): string => {
  const apiError = err as ApiError | undefined
  const body = (apiError?.body || {}) as LicensePolicyErrorBody
  if (body.code === "saas_active_users_limit_reached") {
    return i18n.t("errors.titles.saas_active_users_limit_reached", { defaultValue: "Limit reached" })
  }
  if (body.code === "cooldown_active_reactivation") {
    return i18n.t("errors.titles.cooldown_active_reactivation", { defaultValue: "Reactivation blocked" })
  }
  if (body.license_status || body.degradation_mode) {
    return i18n.t("errors.titles.licenseRestriction", { defaultValue: "License policy restriction" })
  }
  return i18n.t("errors.titles.generic", { defaultValue: "Error" })
}

export const handleError = (err: ApiError, showToast: any) => {
  showToast(getApiErrorTitle(err), getApiErrorMessage(err), "error")
}
